import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icons as I } from '../pack-icons';
import { BrandIcons } from '../pack-icons/brands';

/**
 * WeekStrip — 7-day mini calendar with platform/status dots.
 * days: [{ label:'Mon', date:'May 13', today:bool, slots: [{platform, status, time}] }]
 */
export default function WeekStrip({ days, weekLabel }) {
  const { t } = useTranslation();
  const label = weekLabel ?? t('weekStrip.thisWeek');
  if (!days?.length) {
    return (
      <div className="card week-strip">
        <div className="week-head">
          <div>
            <div className="eyebrow"><span>{t('weekStrip.thisWeek')}</span></div>
            <h3>{t('weekStrip.noScheduledPosts')}</h3>
          </div>
          <Link to="/app/calendar" className="week-link">
            {t('weekStrip.openCalendar')} <I.arrowRight size={12} />
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div className="card week-strip">
      <div className="week-head">
        <div>
          <div className="eyebrow"><span>{label}</span></div>
          <h3>{days[0]?.date && days[days.length - 1]?.date ? `${days[0].date} – ${days[days.length - 1].date}` : t('weekStrip.thisWeek')}</h3>
        </div>
        <Link to="/app/calendar" className="week-link">
          {t('weekStrip.openCalendar')} <I.arrowRight size={12} />
        </Link>
      </div>
      <div className="week-grid">
        {days.map((d, i) => (
          <div key={i} className={`wd ${d.today ? 'is-today' : ''}`}>
            <div className="wd-h">
              <span className="wd-label">{d.label}</span>
              <span className="wd-date">{d.date}</span>
            </div>
            <div className="wd-slots">
              {!d.slots?.length ? (
                <div className="wd-empty">—</div>
              ) : (
                d.slots.map((s, j) => {
                  // If we have a contentItemId, render the slot as a Link
                  // so a click jumps straight into the post preview. The
                  // platform hint scopes the chrome (e.g. Telegram tile
                  // opens with TG branding, not the post's first
                  // publish_target). Slots without a contentItemId
                  // (rare — usually a missing field on the schedule
                  // payload) render as the old static div.
                  const params = new URLSearchParams();
                  if (s.platform) params.set('platform', s.platform);
                  if (s.targetId) params.set('target', s.targetId);
                  const qs = params.toString();
                  const statusTitle = s.status === 'inbox'
                    ? t('postPreview.inboxDraftTitle')
                    : s.status === 'needs_check'
                      ? t('postPreview.statusNeedsCheck')
                      : null;
                  const inner = (
                    <>
                      <span className="wd-slot-time mono">{s.time}</span>
                      <span className="wd-slot-ico">{BrandIcons[s.platform]?.({ size: 11 })}</span>
                    </>
                  );
                  if (s.contentItemId) {
                    return (
                      <Link
                        key={j}
                        to={`/app/post/${encodeURIComponent(s.contentItemId)}${qs ? `?${qs}` : ''}`}
                        className={`wd-slot wd-slot-link wd-${s.status}`}
                        title={statusTitle || t('weekStrip.slotTitlePreview', { platform: s.platform, time: s.time })}
                      >
                        {inner}
                      </Link>
                    );
                  }
                  return (
                    <div key={j} className={`wd-slot wd-${s.status}`} title={statusTitle || t('weekStrip.slotTitle', { platform: s.platform, time: s.time })}>
                      {inner}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
